/**
 * Central configuration for all financial constants used throughout the bond calculator.
 * This module provides a single source of truth for financial calculations,
 * eliminating duplicate hardcoded values across the codebase.
 * Many of these are useful for 'standard' processing, but will allow for user overrides.
 */

/**
 * Time period constants used in bond calculations
 */
export const TIME_PERIODS = {
  /** Standard period length in days (6 months) */
  STANDARD_PERIOD_DAYS: 180,

  /** Months per payment period */
  MONTHS_PER_PERIOD: 6,

  /** Days per year for financial calculations */
  DAYS_PER_YEAR: 360,

  /** Standard project duration in years */
  PROJECT_YEARS: 50,
} as const;

/**
 * Principal and distribution amount constants
 */
export const PRINCIPAL_AMOUNTS = {
  /** Minimum principal amount for bond transactions */
  MINIMUM_PRINCIPAL: 10000,

  /** Principal increment for distribution calculations */
  PRINCIPAL_INCREMENT: 5000,

  /** Balance tolerance for optimization */
  BALANCE_TOLERANCE: 5000,
} as const;

/**
 * Interest rate constants and defaults
 */
export const INTEREST_RATES = {
  /** Default starting interest rate (3%) */
  DEFAULT_STARTING_RATE: 0.03,

  /** Alternative starting rate (4%) */
  ALTERNATIVE_STARTING_RATE: 0.04,

  /** Default maximum interest rate (5%) */
  DEFAULT_MAX_RATE: 0.05,

  /** Maximum interest rate assumption for capacity calculations (6%) */
  MAX_INTEREST_RATE_ASSUMPTION: 0.06,

  /** Annual increment amount for interest rates (0.1%) */
  DEFAULT_INCREMENT_AMOUNT: 0.0005,
} as const;

/**
 * Buffer and threshold percentages
 */
export const BUFFER_PERCENTAGES = {
  /** Capacity buffer percentage (10%) */
  CAPACITY_BUFFER_PERCENTAGE: 0.1,

  /** DRF balance status thresholds */
  DRF_STATUS_LOW_THRESHOLD: 0.33, // Below 33% = Low
  DRF_STATUS_MEDIUM_THRESHOLD: 0.67, // 33-67% = Medium
  // Above 67% = High
} as const;

/**
 * DRF (Debt Reserve Fund) limits and thresholds
 */
export const DRF_LIMITS = {
  /** Standard minimum balance for DRF */
  STANDARD_MINIMUM_BALANCE: 250000,

  /** Low minimum balance for DRF */
  LOW_MINIMUM_BALANCE: 50000,

  /** Zero minimum balance (no minimum) */
  ZERO_MINIMUM_BALANCE: 0,

  /** Standard maximum balance for DRF */
  STANDARD_MAXIMUM_BALANCE: 2500000,

  /** Medium maximum balance for DRF */
  MEDIUM_MAXIMUM_BALANCE: 1000000,

  /** DRF buffer threshold for utilization analysis */
  DRF_BUFFER_THRESHOLD: 5000,

  /**
   * Low starting DRF threshold for optimization strategy selection.
   * If starting DRF <= this value, use MILLAGE_REDISTRIBUTION to reduce ending DRF.
   * If starting DRF > this value, use UTILIZATION to add principal.
   */
  LOW_STARTING_DRF_THRESHOLD: 100000,
} as const;

/**
 * Optimization and processing constants
 */
export const OPTIMIZATION_CONSTANTS = {
  /** Maximum iterations for optimization algorithms */
  MAX_ITERATIONS: 5,

  /** Convergence tolerance for distribution calculations */
  CONVERGENCE_TOLERANCE: 1.0,

  /** Target value strategy improvement threshold (0.1%) */
  TARGET_VALUE_IMPROVEMENT_THRESHOLD: 0.001,

  /** Max value strategy improvement threshold ($0.01) */
  MAX_VALUE_IMPROVEMENT_THRESHOLD: 0.01,

  /** Precision constants for comparison */
  PRECISION_CONSTANTS: {
    /** Currency comparison epsilon (1 cent) */
    CURRENCY_EPSILON: 0.01,

    /** Percentage comparison epsilon (0.0001%) */
    PERCENTAGE_EPSILON: 0.000001,

    /** Interest comparison epsilon ($1) */
    INTEREST_EPSILON: 1.0,

    /** Principal comparison epsilon ($1) */
    PRINCIPAL_EPSILON: 1.0,
  },
} as const;

/**
 * Targeted bond processing configuration
 * Used by TargetedBondAdjuster for adjusting candidates with targeted bonds during shape search
 */
export const TARGETED_BOND_PROCESSING = {
  /** Minimum adjustment amount for targeted bond processing ($5,000) */
  MIN_ADJUSTMENT: 5000,

  /** DRF safety buffer for targeted bond optimization ($10,000) */
  DRF_SAFETY_BUFFER: 10000,

  /** Maximum iterations per bond during targeted processing */
  MAX_ITERATIONS_PER_BOND: 2,
} as const;

/**
 * Constraint constants for revenue distribution
 * Used to prevent over-distribution of principal that would cause DRF to go negative
 */
export const DISTRIBUTION_CONSTRAINTS = {
  /**
   * Safety buffer for minimum payment constraint ($1,000)
   * Ensures we don't distribute principal that would leave any payment
   * with less than this buffer available after interest cascading
   */
  MINIMUM_PAYMENT_SAFETY_BUFFER: 1000,
} as const;

/**
 * UI input step values for form controls
 */
export const UI_STEP_VALUES = {
  /** Step value for interest rate inputs (0.1%) */
  INTEREST_RATE_STEP: 0.001,

  /** Step value for increment amount inputs (0.01%) */
  INCREMENT_AMOUNT_STEP: 0.0001,

  /** Step value for millage rate inputs */
  MILLAGE_RATE_STEP: 0.1,

  /** Step value for currency/percentage inputs */
  CURRENCY_STEP: 0.01,
} as const;
